import { PageController } from '@client/components/_core/layout/PageController';
import { BasicTabs } from '@client/components/_core/navigation/Tabs';
import { AiAssistantButton } from '@client/components/_shared/ai/AiAssistantButton';
import { STREAMED_ANALYSIS_PROGRESS_POLL_MS } from '@client/components/_shared/ai/streamed-analysis-progress';
import { EntityDetailsToolbar } from '@client/components/_ui/data-display/EntityDetailsToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Alerts } from '@client/components/_ui/display/Alerts';
import { UpgradeTag } from '@client/components/_ui/display/UpgradeTag';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { message } from '@client/components/_ui/feedback/message';
import { ComplaintFlagManage } from '@client/components/complaints/ComplaintFlagManage';
import { DetectedBreachAlert } from '@client/components/compliance/DetectedBreachAlert';
import { useQueryErrorEffect } from '@client/hooks/api/useQueryErrorEffect';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePendingTimelineCloseGuard } from '@client/hooks/business/milestone/usePendingTimelineCloseGuard';
import { useIsLiteOrg } from '@client/hooks/business/org/useIsLiteOrg';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import type { FileRouteTypes } from '@client/routeTree.gen';
import { selectOrg, selectUser, useAuthStore } from '@client/stores/AuthStore';
import { ICONS } from '@client/styles/theme';
import { DETAIL_RECORD_PREFETCH_OPTIONS, DETAIL_RECORD_QUERY_OPTIONS } from '@client/trpc/query-options';
import type { FieldError } from '@client/types/error';
import type { ShareableRouteContext } from '@client/types/router';
import { capture } from '@client/utils/analytics';
import { TokenRefreshManager } from '@client/utils/auth/TokenRefreshManager';
import { showLiteUpgradePopover } from '@client/utils/domain/lite';
import { COMPLAINT_STATUS, ORG_ROLES } from '@shared/constants';
import { COMM_CHANNELS } from '@shared/constants/business/communication';
import { isActiveComplaintAiAnalysisStatus } from '@shared/constants/business/complaint-ai-analysis';
import { hasPermission } from '@shared/permissions/roles';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router';
import { Skeleton } from 'antd';
import { CheckCircle, Download, Flag, Mail, MessageCircle, NotebookPen, RotateCcw, WandSparkles } from 'lucide-react';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { match } from 'ts-pattern';
import {
    runDetailRoutePrefetchWhenAuthorised,
    shouldPrefetchDetailRouteData,
    yieldToPrefetchBatch,
} from '../../../detail-route-prefetch';
import { useValidateComplaintBeforeClosing } from '../hooks';
import { CloseComplaintDrawer } from './CloseComplaintDrawer';
import type { ComplaintAnalysisDrawerAction } from './ComplaintAnalysisDrawer';
import { prefetchComplaintManageLookups } from './complaint-details-prefetch';
import { Header } from './Header';
import { useCanEditComplaint, useExportComplaint } from './hooks';
import { LazyComplaintSendModal } from './LazyComplaintSendModal';
import { complaintFormStore } from './store';
import { useComplaintAnalysisProgressStream } from './useComplaintAnalysisProgressStream';

// Lazy-load the heavy AI analysis drawer — it renders >900 lines of JSX
// (Markdown, RichText, JsonView, debug panels, similar-complaint cards) and
// fires 2 expensive tRPC queries (getComplaintAiAnalysis, findSimilarComplaints)
// on mount. Mounting it eagerly cost every visit ~1-2s of unnecessary parse
// work and contributed to the page's p75 INP of 700ms.
const ComplaintAnalysisDrawer = lazy(() =>
    import('./ComplaintAnalysisDrawer').then((m) => ({ default: m.ComplaintAnalysisDrawer })),
);

// Tab keys map 1:1 to this route's child routes; the typed map keeps every tab
// destination checked against the generated route tree, so a child-route rename
// fails typecheck instead of shipping a dead tab.
type ComplaintTabTo = Extract<FileRouteTypes['to'], `/console/$orgId/compliance/complaints/details/$id/${string}`>;

const COMPLAINT_TAB_ROUTES = {
    manage: '/console/$orgId/compliance/complaints/details/$id/manage',
    comms: '/console/$orgId/compliance/complaints/details/$id/comms',
    library: '/console/$orgId/compliance/complaints/details/$id/library',
    history: '/console/$orgId/compliance/complaints/details/$id/history',
} as const satisfies Record<string, ComplaintTabTo>;

type ComplaintTabKey = keyof typeof COMPLAINT_TAB_ROUTES;

export const Route = createFileRoute('/console/$orgId/compliance/complaints/details/$id')({
    staticData: { breadcrumb: { title: 'Complaint details' }, ui: { widthVariant: 'default' }, entity: 'complaint' },
    params: {
        parse({ id }) {
            return { id: BigInt(id) };
        },
    },
    beforeLoad: ({
        context,
        params,
        preload,
        matches,
        abortController,
        cause,
    }): ShareableRouteContext | Promise<ShareableRouteContext> => {
        // Kick off the LCP-critical `getComplaint` (and the manage tab's org-wide
        // lookups) while the route's chunks are still being parsed and the auth
        // gate is still rendering — otherwise none of them start until the
        // component mounts. Same pattern as `~console.$orgId/~index.tsx`.
        // (getComplaintAiAnalysis is flag-gated at mount, so it is deliberately
        // not warmed here.)
        // On a cold email deep link the session is still restoring here, so the
        // prefetch is deferred until the auth store becomes authorised. Hover-intent
        // preloads never defer (each hovered row would queue a speculative burst
        // behind the next token refresh); leaving the match aborts the deferral.
        // The bare details path redirects to the Manage tab, so it shows it too.
        const showsManageTab = matches.some(
            ({ routeId }) =>
                routeId === '/console/$orgId/compliance/complaints/details/$id/manage' ||
                routeId === '/console/$orgId/compliance/complaints/details/$id/',
        );
        runDetailRoutePrefetchWhenAuthorised(
            () => {
                const authState = useAuthStore.getState();
                const user = selectUser(authState);
                return shouldPrefetchDetailRouteData({
                    hasValidToken: authState.hasValidToken({
                        minValidityMs: TokenRefreshManager.REFRESH_BUFFER_MS,
                    }),
                    userRoles: user?.roles,
                    allowedRoles: [ORG_ROLES.USER, ORG_ROLES.SHARED_EDIT_USER, ORG_ROLES.ORG_ADMIN],
                    hydratedOrg: selectOrg(authState),
                    routeOrgId: params.orgId,
                    requiredModule: 'compliance',
                });
            },
            () => {
                void context.trpcUtils.complaint.getComplaint.prefetch(
                    { id: params.id },
                    DETAIL_RECORD_PREFETCH_OPTIONS,
                );
                prefetchComplaintManageLookups(params.id, { preload, showsManageTab });
            },
            { defer: !preload, signal: abortController.signal },
        );
        return cause === 'enter' ? yieldToPrefetchBatch() : undefined;
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ComplaintDetails />
        </PageController>
    );
}

function ComplaintDetails() {
    const orgId = useOrgId();
    const user = useUser();
    const enableComplaintAiReport = useFeatureFlag('enable-complaint-ai-report');
    const enableComplaintCommunications = useFeatureFlag('enable-complaint-communications', false);
    const isLiteOrg = useIsLiteOrg();
    const { id } = Route.useParams();
    const { trpcUtils, trpc } = useTrpc();
    const canEdit = useCanEditComplaint();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [exportComplaint, { loading: exportComplaintLoading }] = useExportComplaint();
    const [complaintAiReportAction, setComplaintAiReportAction] = useState<ComplaintAnalysisDrawerAction>(['close']);
    // Mount the AI drawer lazily on first open and keep it mounted thereafter
    // so the drawer's close animation can play. The expensive AI queries
    // (getComplaintAiAnalysis, findSimilarComplaints) are gated inside the
    // component on action[0] === 'open', so deferring the mount also defers
    // the queries.
    const [hasOpenedAiReport, setHasOpenedAiReport] = useState(false);
    useEffect(() => {
        if (complaintAiReportAction[0] === 'open') setHasOpenedAiReport(true);
    }, [complaintAiReportAction]);
    const { validateRequiredFields, loading: validateComplaintBeforeClosingLoading } =
        useValidateComplaintBeforeClosing();
    const { blockCloseIfPendingTimeline, loading: pendingTimelineLoading } = usePendingTimelineCloseGuard({
        entity: 'complaint',
        entityId: id,
    });

    const [requiredFieldsErrors, setRequiredFieldsErrors] = useState<FieldError[]>([]);
    const [isComplaintFlagManageOpen, setIsComplaintFlagManageOpen] = useState(false);
    const [composeChannel, setComposeChannel] = useState<typeof COMM_CHANNELS.EMAIL | typeof COMM_CHANNELS.SMS | null>(
        null,
    );

    const updateComplaintMutation = trpc.complaint.updateComplaint.useMutation();

    // Fetch complaint data with tRPC query
    const complaintQuery = trpc.complaint.getComplaint.useQuery(
        { id },
        {
            enabled: !!id,
            ...DETAIL_RECORD_QUERY_OPTIONS,
            // AI flag suggestion runs as the last step of the async post-create workflow
            // (suggestVulnerabilityComplaintFlags) and there's no per-complaint status sentinel to watch,
            // so poll briefly after creation to surface flags without a manual reload.
            refetchInterval: (query) => {
                const createdAt = query.state.data?.createdAt;
                if (!createdAt) return false;
                const ageMs = Date.now() - new Date(createdAt).getTime();
                return ageMs < 60_000 ? 2_000 : false;
            },
        },
    );
    useQueryErrorEffect(complaintQuery.error, (error) => {
        message.destroy();

        match(error.data?.errorId)
            .with('recordNotFound', () => {
                void errorMessage
                    .showAsync(
                        "The complaint you are trying to access either doesn't exist or you don't have permissions to access it.",
                    )
                    .then(() => {
                        navigate({ to: '/console/$orgId/compliance/complaints', params: { orgId } });
                    });
            })
            .otherwise(() => {
                errorMessage.show(error);
            });
    });

    const { formData: complaint, save } = complaintFormStore.useInit({
        entity: 'complaint',
        data: complaintQuery.data,
        isLoading: complaintQuery.isLoading,

        async onReload() {
            await complaintQuery.refetch();
        },

        async onSave(currentValue, changedValues) {
            try {
                // Convert null status to undefined since the API doesn't accept null for status
                const { status, ...restChangedValues } = changedValues;
                const set = {
                    ...restChangedValues,
                    ...(status !== undefined && { status: status ?? undefined }),
                };
                await updateComplaintMutation.mutateAsync({ id: currentValue.id, set });

                if (changedValues.status) {
                    capture('complaint_status_changed', {
                        entity_id: currentValue.id.toString(),
                        to_status: changedValues.status,
                        module: 'compliance',
                    });
                }

                if (changedValues.status === COMPLAINT_STATUS.RE_OPENED) {
                    message.success('Complaint re-opened successfully.');
                }

                if (changedValues.status === COMPLAINT_STATUS.CLOSED) {
                    message.success('Complaint closed successfully.');
                }

                await trpcUtils.complaint.invalidate();

                return { success: true };
            } catch (error: unknown) {
                return { success: false, error: error instanceof Error ? error : new Error(String(error)) };
            }
        },
    });

    const canUpdateComplaint = useMemo(() => {
        if (!complaint) {
            return false;
        }

        const hasOrgAdminPermission = hasPermission(user.roles, 'org:admin');
        const hasUpdatePermission = hasPermission(user.roles, 'complaint:update');
        const hasUpdateOwnHandlingPartiesPermission =
            hasPermission(user.roles, 'complaint:update:own_handling_parties') &&
            complaint.handlingPartyIds?.some((item) => user.handlingPartyIds.includes(item));
        const hasUpdateIsOwnerPermission =
            hasPermission(user.roles, 'complaint:update:is_owner') && complaint.ownerId === user.id;

        return (
            hasOrgAdminPermission ||
            hasUpdatePermission ||
            hasUpdateOwnHandlingPartiesPermission ||
            hasUpdateIsOwnerPermission
        );
    }, [complaint, user]);

    // Fetch AI analysis from relational tables (separate from complaint query).
    // Gated on `enable-complaint-ai-report` — the same flag that gates every
    // consumer of the data (breach badge + alert below, the analysis drawer) so
    // orgs without AI features don't pay the cost on every detail-page batch.
    const aiAnalysisEnabled = enableComplaintAiReport;
    // While a run is in progress its stages are pushed over a stream, and both polls of the analysis
    // (here and in the analysis drawer) slow to a safety net. The stream needs the run's id from the
    // poll, so whether it is connected reaches the cadence a render later.
    const [isAnalysisProgressLive, setIsAnalysisProgressLive] = useState(false);
    const { data: aiAnalysis } = trpc.complaint.getComplaintAiAnalysis.useQuery(
        id && aiAnalysisEnabled ? { complaintId: id } : skipToken,
        {
            refetchInterval: (query) => {
                if (!isActiveComplaintAiAnalysisStatus(query.state.data?.status)) return false;
                return isAnalysisProgressLive ? STREAMED_ANALYSIS_PROGRESS_POLL_MS : 2000;
            },
        },
    );
    const { live: analysisProgressStreamLive } = useComplaintAnalysisProgressStream(
        id && aiAnalysisEnabled ? id : null,
        isActiveComplaintAiAnalysisStatus(aiAnalysis?.status) ? aiAnalysis.uuid : undefined,
    );
    useEffect(() => setIsAnalysisProgressLive(analysisProgressStreamLive), [analysisProgressStreamLive]);

    // Computed state: Count detected breaches from relational AI analysis
    const detectedBreaches = useMemo(() => {
        if (aiAnalysis?.status !== 'COMPLETED') {
            return { count: 0, codes: [] };
        }
        const breachedObligations = aiAnalysis.obligations.filter((o) => o.isBreach && !o.isResolved);
        return {
            count: breachedObligations.length,
            codes: breachedObligations.map((o) => o.identificationCode),
        };
    }, [aiAnalysis]);

    const reopenComplaint = async () => {
        await save({ status: COMPLAINT_STATUS.RE_OPENED });
    };

    const closeComplaint = async () => {
        if (!complaint) {
            return;
        }

        // Run the timeline guard first (mirrors the incident close flow) so pending timeline items
        // always surface the guided modal rather than being pre-empted by the product check.
        if (await blockCloseIfPendingTimeline()) {
            return;
        }

        const products = complaint.products;
        if (!products || (Array.isArray(products) && products.length === 0)) {
            errorMessage.show('Please select a product or service before closing the complaint.');
            return;
        }

        const requiredFieldsErrors = validateRequiredFields(complaint);

        if (requiredFieldsErrors.length) {
            setRequiredFieldsErrors(requiredFieldsErrors);
            return;
        }

        await save({ status: COMPLAINT_STATUS.CLOSED });
    };

    return (
        <>
            {complaint && (
                <CloseComplaintDrawer
                    open={!!requiredFieldsErrors.length}
                    onSave={async (data) => {
                        await save({ ...data, status: COMPLAINT_STATUS.CLOSED });
                    }}
                    onClose={() => {
                        setRequiredFieldsErrors([]);
                    }}
                    errors={requiredFieldsErrors}
                />
            )}
            {hasOpenedAiReport && (
                <Suspense
                    fallback={
                        <div className="fixed top-4 right-4 z-[1100]">
                            <Loading size="small" />
                        </div>
                    }
                >
                    <ComplaintAnalysisDrawer
                        action={complaintAiReportAction}
                        canManageAnalysis={canUpdateComplaint}
                        isAnalysisProgressLive={isAnalysisProgressLive}
                        onClose={() => {
                            setComplaintAiReportAction(['close']);
                        }}
                    />
                </Suspense>
            )}

            {complaint && id === complaint.id ? <Header aiAnalysisStatus={aiAnalysis?.status ?? null} /> : null}

            {complaint && id === complaint.id ? (
                <ComplaintFlagManage
                    isOpen={isComplaintFlagManageOpen}
                    onClose={() => setIsComplaintFlagManageOpen(false)}
                    complaintId={complaint.id}
                    isReadOnly={!canEdit}
                />
            ) : null}

            {complaint && id === complaint.id && enableComplaintCommunications && composeChannel ? (
                <LazyComplaintSendModal
                    open
                    channel={composeChannel}
                    onClose={() => setComposeChannel(null)}
                    complaintId={complaint.id}
                    complaint={complaint}
                    onSent={() => {
                        void trpcUtils.complaint.listComplaintCommThreads.invalidate();
                        // Facets feed the From/To filters; a new message can introduce a participant.
                        void trpcUtils.complaint.getComplaintCommFacets.invalidate();
                    }}
                />
            ) : null}

            {/* AI-detected breaches alert banner */}
            {complaint && id === complaint.id && !!detectedBreaches.count && enableComplaintAiReport && (
                <DetectedBreachAlert
                    title={
                        <>
                            {detectedBreaches.count} AI-detected potential breach
                            {detectedBreaches.count > 1 ? 'es' : ''}
                        </>
                    }
                    description="Our AI has identified potential compliance breach(es) based on this complaint. Review the analysis to understand the implications."
                    actionLabel="Review analysis"
                    className="mb-6"
                    onAction={() => {
                        setComplaintAiReportAction(['open', id]);
                        capture('detected_breach_alert_clicked', {
                            complaint_id: id.toString(),
                            org_id: orgId.toString(),
                            breach_count: detectedBreaches.count,
                        });
                    }}
                />
            )}

            {complaint && id === complaint.id ? (
                <EntityDetailsToolbar
                    actions={
                        <>
                            {enableComplaintCommunications &&
                                // Curium Lite (CUR-379): communications are premium — crown mini-badges on
                                // the quick actions, clicks open the contact page. Server refuses the comm
                                // procedures for Lite orgs regardless.
                                (isLiteOrg ? (
                                    <>
                                        <span className="relative inline-flex">
                                            <Button.Icon
                                                icon={<Mail />}
                                                color="gray"
                                                bordered
                                                tooltip="Send email — upgrade to unlock"
                                                onClick={(event) =>
                                                    showLiteUpgradePopover('complaint-send-email', event.currentTarget)
                                                }
                                            />
                                            <UpgradeTag mini className="absolute -top-1.5 -right-1.5" />
                                        </span>
                                        <span className="relative inline-flex">
                                            <Button.Icon
                                                icon={<MessageCircle />}
                                                color="gray"
                                                bordered
                                                tooltip="Send SMS — upgrade to unlock"
                                                onClick={(event) =>
                                                    showLiteUpgradePopover('complaint-send-sms', event.currentTarget)
                                                }
                                            />
                                            <UpgradeTag mini className="absolute -top-1.5 -right-1.5" />
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <Button.Icon
                                            icon={<Mail />}
                                            color="gray"
                                            bordered
                                            tooltip="Send email"
                                            onClick={() => setComposeChannel(COMM_CHANNELS.EMAIL)}
                                        />
                                        <Button.Icon
                                            icon={<MessageCircle />}
                                            color="gray"
                                            bordered
                                            tooltip="Send SMS"
                                            onClick={() => setComposeChannel(COMM_CHANNELS.SMS)}
                                        />
                                    </>
                                ))}
                            {canEdit && (
                                <Button.Icon.Link
                                    icon={<NotebookPen />}
                                    color="gray"
                                    bordered
                                    tooltip="Add note"
                                    to="/console/$orgId/compliance/complaints/details/$id/manage/note/add"
                                    params={{ orgId, id }}
                                />
                            )}
                            <Button.Icon
                                icon={<Flag />}
                                color="gray"
                                bordered
                                tooltip="Manage flags"
                                onClick={() => setIsComplaintFlagManageOpen(true)}
                            />
                            <Button.Icon
                                icon={<Download />}
                                color="gray"
                                bordered
                                tooltip="Download complaint"
                                loading={exportComplaintLoading}
                                onClick={() => void exportComplaint(id)}
                            />
                        </>
                    }
                    terminal={
                        <>
                            <AiAssistantButton
                                entityType="complaint"
                                entityId={complaint.id}
                                entityName="Complaint"
                                outputFormat="markdown"
                            />
                            {enableComplaintAiReport && (
                                <Button
                                    type="secondary"
                                    icon={<WandSparkles />}
                                    onClick={() => {
                                        capture('analyze_complaint_clicked', {
                                            complaint_id: id.toString(),
                                            complaint_status: complaint.status ?? undefined,
                                            org_id: orgId.toString(),
                                        });
                                        setComplaintAiReportAction(['open', id]);
                                    }}
                                >
                                    Analyse complaint
                                </Button>
                            )}
                            {complaint.status !== COMPLAINT_STATUS.CLOSED && canEdit && (
                                <Button
                                    type="danger-outline"
                                    loading={validateComplaintBeforeClosingLoading || pendingTimelineLoading}
                                    icon={<CheckCircle />}
                                    onClick={closeComplaint}
                                >
                                    Close complaint
                                </Button>
                            )}
                            {complaint.status === COMPLAINT_STATUS.CLOSED && canUpdateComplaint && (
                                <Button type="secondary" icon={<RotateCcw />} onClick={reopenComplaint}>
                                    Open complaint
                                </Button>
                            )}
                        </>
                    }
                />
            ) : null}

            {complaint && id === complaint.id ? (
                <BasicTabs
                    type="line"
                    destroyOnHidden
                    activeKey={pathname.split('/')[7]}
                    tabBarExtraContent={{
                        left: (
                            <div className="mx-4 size-4">
                                <Alerts id={id} entity="complaint" indicatorColor={complaint.indicatorColor} key={id} />
                            </div>
                        ),
                    }}
                    onTabClick={(key, event) => {
                        const tabKey = key as ComplaintTabKey;

                        // Curium Lite (CUR-379): the Communication tab is premium — the crowned
                        // tab opens the upgrade popover instead of navigating.
                        if (isLiteOrg && tabKey === 'comms') {
                            showLiteUpgradePopover('complaint-comms-tab', event.currentTarget);
                            return;
                        }
                        navigate({
                            to: COMPLAINT_TAB_ROUTES[tabKey],
                            params: { orgId, id },
                        });
                    }}
                    items={[
                        {
                            key: 'manage' satisfies ComplaintTabKey,
                            label: (
                                <span className="flex items-center gap-2">
                                    <ICONS.manage size={16} />
                                    Manage complaint
                                </span>
                            ),
                            children: <Outlet />,
                        },
                        ...(enableComplaintCommunications
                            ? [
                                  {
                                      key: 'comms' satisfies ComplaintTabKey,
                                      label: (
                                          <span className="flex items-center gap-2">
                                              <Mail size={16} />
                                              Communication
                                              {isLiteOrg && <UpgradeTag className="mr-0" />}
                                          </span>
                                      ),
                                      children: <Outlet />,
                                  },
                              ]
                            : []),
                        {
                            key: 'library' satisfies ComplaintTabKey,
                            label: (
                                <span className="flex items-center gap-2">
                                    <ICONS.library size={16} />
                                    Library
                                </span>
                            ),
                            children: <Outlet />,
                        },
                        {
                            key: 'history' satisfies ComplaintTabKey,
                            label: (
                                <span className="flex items-center gap-2">
                                    <ICONS.history size={16} />
                                    History
                                </span>
                            ),
                            children: <Outlet />,
                        },
                    ]}
                />
            ) : (
                <Skeleton active />
            )}
        </>
    );
}
